import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse, HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { catchError, switchMap, first, filter, take } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { TokenManagerService } from '../auth/token-manager.service';
import { AuthService } from '../auth/auth.service';
import { environment } from '../../../environments/environment';
import { ToastService } from '../toast/toast.service';

/**
 * Patterns for public endpoints that can be accessed without authentication.
 * If auth fails on these endpoints, the request will be retried without auth headers.
 */
const PUBLIC_ENDPOINT_PATTERNS = [
	{ pattern: /\/api\/horse_listings\/[^/]+\/?$/, methods: ['GET'] },
	{ pattern: /\/api\/horse_listings\/?$/, methods: ['GET'] },
	{ pattern: /\/api\/products\/?$/, methods: ['GET'] },
	{ pattern: /\/api\/products\/[^/]+\/?$/, methods: ['GET'] },
	{ pattern: /\/api\/tack_listings\/[^/]+\/?$/, methods: ['GET'] },
	{ pattern: /\/api\/tack_listings\/?$/, methods: ['GET'] },
	{ pattern: /\/api\/users\/[^/]+\/?$/, methods: ['GET'] },
];

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
	constructor(
		private authService: AuthService,
		private toastService: ToastService,
		private tokenManager: TokenManagerService
	) {}

	/**
	 * Check if the given URL and method represent a public endpoint.
	 * Public endpoints can be accessed without authentication.
	 */
	private isPublicEndpoint(url: string, method: string): boolean {
		return PUBLIC_ENDPOINT_PATTERNS.some(
			(ep) => ep.pattern.test(url) && ep.methods.includes(method.toUpperCase())
		);
	}

	/**
	 * Retry a request without authentication headers.
	 * Used for public endpoints when auth fails.
	 */
	private retryWithoutAuth(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		console.log('[TokenInterceptor] Retrying public endpoint without auth:', request.url);
		const unauthRequest = request.clone({
			headers: request.headers.delete('Authorization'),
		});
		return next.handle(unauthRequest);
	}

	intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		// Bypass auth ready gate and token addition for auth endpoints
		// Include OAuth endpoints to allow social login to work before authReady$ emits
		if (request.url.includes('/auth/jwt/refresh/') || 
			request.url.includes('/auth/jwt/create/') ||
			request.url.includes('/auth/oauth/')) {
			request = this.setHeaders(request);
			return next.handle(request);
		}

		// Wait for initial auth check to complete before processing any API requests.
		// This ensures that:
		// 1. If a token exists and needs refresh, the refresh completes first
		// 2. API calls don't fire with expired tokens before the startup refresh
		// 3. All requests use the latest valid token after auth initialization
		return this.authService.authReady$.pipe(
			take(1),
			switchMap(() => {
				return this.tokenManager.getAccessToken().pipe(
					first(),
					switchMap((token: string | null) => {
						if (token) {
							// Determine auth scheme based on token_type (Bearer for OAuth, JWT for email login)
							const tokenType = this.authService.token?.token_type;
							const authScheme = tokenType?.toLowerCase() === 'bearer' ? 'Bearer' : 'JWT';

							request = request.clone({
								setHeaders: {
									Authorization: `${authScheme} ${token}`,
									'Content-Type': 'application/json',
									Accept: `application/json; version=${environment.api_version}`,
								},
							});
						} else {
							// No token available - set headers without auth
							request = this.setHeaders(request);
						}

						return next.handle(request).pipe(
							catchError((error: HttpErrorResponse) => {
								if (error.status === 401) {
									return this.handle401Error(request, next);
								}
								return throwError(() => error);
							})
						);
					})
				);
			})
		);
	}

	private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		const isPublic = this.isPublicEndpoint(request.url, request.method);

		if (this.tokenManager.isRefreshing) {
			// Queue subsequent requests while refresh is in progress
			// Wait for the refreshToken$ to emit a non-null value (the new token)
			// or emit null on failure, then process the queued request
			return this.tokenManager.refreshToken$.pipe(
				filter((token: string | null) => token !== null), // Skip null emissions, wait for actual token
				take(1), // Only take the first valid token emission
				switchMap((newToken: string | null) => {
					if (newToken) {
						// Determine auth scheme based on token_type
						const tokenType = this.authService.token?.token_type;
						const authScheme = tokenType?.toLowerCase() === 'bearer' ? 'Bearer' : 'JWT';

						request = request.clone({
							setHeaders: { Authorization: `${authScheme} ${newToken}` },
						});
						return next.handle(request);
					}
					// This shouldn't happen due to the filter above, but safety check
					return throwError(() => new Error('Token refresh failed'));
				}),
				catchError((err: any) => {
					// If refresh failed and this is a public endpoint, retry without auth
					if (isPublic) {
						return this.retryWithoutAuth(request, next);
					}
					return throwError(() => err);
				})
			);
		}

		// First request to fail with 401 - initiate token refresh via TokenManagerService
		// TokenManagerService.refreshToken() handles setting isRefreshing and notifying subscribers
		return this.tokenManager.refreshToken().pipe(
			switchMap((newToken: string) => {
				// Determine auth scheme based on token_type
				const tokenType = this.authService.token?.token_type;
				const authScheme = tokenType?.toLowerCase() === 'bearer' ? 'Bearer' : 'JWT';

				request = request.clone({
					setHeaders: { Authorization: `${authScheme} ${newToken}` },
				});
				return next.handle(request);
			}),
			catchError((err: any) => {
				// If this is a public endpoint, retry without auth before giving up
				if (isPublic) {
					console.log('[TokenInterceptor] Auth refresh failed for public endpoint, retrying without auth');
					return this.retryWithoutAuth(request, next);
				}

				// Only logout if it's actually an auth/token issue
				// Check if error indicates token is invalid or refresh failed
				const isAuthError = err?.status === 401 ||
					err?.error?.code === 'token_not_valid' ||
					err?.message?.includes('refresh') ||
					err?.message?.includes('token');

				if (isAuthError) {
					// Use silent logout since we're showing our own toast
					this.authService.logout({ silent: true });
					this.toastService.show('Session expired. Please log in again.');
				}

				return throwError(() => err);
			})
		);
	}

	private setHeaders(request: HttpRequest<any>): HttpRequest<any> {
		return request.clone({
			headers: request.headers.set('Content-Type', 'application/json').set('Accept', `application/json; version=${environment.api_version}`),
		});
	}
}

// import { Injectable } from '@angular/core';
// import { HttpClient, HttpHeaders, HttpErrorResponse, HttpResponse, HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
// import { catchError, tap, switchMap, mergeMap, concatMap, exhaustMap, map, first } from 'rxjs/operators';
// import { Observable, throwError, of, Subscription } from 'rxjs';
// import { AuthService } from '../auth/auth.service';
// import { Token } from '../auth/auth.types';
// import { environment } from '../../../environments/environment';
// import { ToastService } from '../toast/toast.service';
// import { Store } from '@ngrx/store';
// import * as fromApp from '../../app.reducer';

// @Injectable()
// export class TokenInterceptor implements HttpInterceptor {
// 	storeSubscription: Subscription;
// 	lastToken = '';

// 	constructor(
// 		private authService: AuthService,
// 		private toastService: ToastService,
// 		private store: Store<fromApp.AppState>
// 	) {
// 		console.log('token interceptor constructor');
// 	}

// 	intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
// 		console.log('token-interceptor is listening...');

// 		/* Bypass token addition for renewals */
// 		if (request.url.includes('/auth/jwt/refresh/') || request.url.includes('/auth/jwt/create/')) {
// 			console.log('Bypassing token addition for refresh endpoint');
// 			// Keep original content-type and accept headers but don't add auth
// 			request = request.clone({
// 				headers: request.headers.set('Content-Type', 'application/json').set('Accept', 'application/json; version=' + environment.api_version),
// 			});
// 			return next.handle(request);
// 		}

// 		return this.store.select('auth').pipe(
// 			first(),
// 			mergeMap((token: Token | undefined) => {
// 				if (token && token.access) {
// 					if (token.access !== this.lastToken) {
// 						console.log('%cInterceptor USING NEW TOKEN', 'font-size: 1.6rem; color:green; background:black;');
// 					}

// 					console.log('adding token to request...');
// 					console.log(token.access);

// 					request = request.clone({
// 						headers: request.headers
// 							.set('Content-Type', 'application/json')
// 							.set('Accept', 'application/json; version=' + environment.api_version)
// 							.set('Authorization', 'JWT ' + token.access),
// 					});

// 					this.lastToken = token.access;
// 				} else {
// 					console.log('%cInterceptor NO TOKEN EMITTED', 'font-size: 1.2rem; color:yellow; background:black;');
// 					this.lastToken = '';
// 				}

// 				if (token.access !== this.lastToken) {
// 					console.log('%cInterceptor USING NEW TOKEN', 'font-size: 1.6rem; color:green; background:black;');
// 				}

// 				return next.handle(request).pipe(
// 					catchError((err: any): Observable<any> => {
// 						if (err instanceof HttpErrorResponse) {
// 							console.log('<<<<<<<<<<<<<<<<<<<<<<<->>>>>>>>>>>>>>>>>>>>>>>');
// 							console.log('<<<<<<<<<<<<<<< NETWORK ERROR >>>>>>>>>>>>>>>>>');
// 							console.log('<<<<<<<<<<<<<<<<<<<<<<<->>>>>>>>>>>>>>>>>>>>>>>');
// 							switch ((err as HttpErrorResponse).status) {
// 								case 400:
// 									return this.handle400Error(err);
// 								case 401:
// 									return this.handle401Error(err, request, next);
// 								default:
// 									return throwError(err);
// 							}
// 						}
// 					})
// 				);
// 			})
// 		);
// 	}

// 	handle400Error(error: HttpErrorResponse): Observable<any> {
// 		console.log('handle400Error()');
// 		console.log(error);
// 		return throwError(error);
// 	}

// 	handle401Error(error: HttpErrorResponse, request: HttpRequest<any>, next: HttpHandler): Observable<any | HttpEvent<any>> {
// 		// returned a 401 after including the auth token
// 		// check if the reason is due to bad auth token
// 		// if so, attempt renewal of auth.refresh
// 		// on failure, logout
// 		console.log('handle401Error()');
// 		console.log(error);
// 		console.log(error.error);

// 		// is the 401 due to auth token failure? (could also happen with resource missing?)
// 		// need to wrap following block with above logic.
// 		console.log('interceptor will try renewToken');
// 		if (!this.authService.token || !this.authService.token.refresh) {
// 			console.log('no refresh token is set!');
// 			// initial login hits this currently,
// 			// but that could be a direct call with unauth
// 			console.log(this.authService.token);
// 			return of(error);
// 		}

// 		return this.authService.renewToken().pipe(
// 			switchMap((data) => {
// 				console.log('renewToken data');
// 				console.log('NEW TOKEN ARRIVAL, applying to request....');
// 				console.log(data.access);

// 				if (data && data.access && data.refresh) {
// 					console.log('FIXED THE TOKEN!');
// 					console.log('returning cloned request with freshly returned token...');
// 					// request = request.clone({ headers: request.headers.set('Authorization', 'JWT ' + c.access ) });
// 					return next.handle(
// 						request.clone({
// 							setHeaders: { authorization: 'JWT ' + data.access },
// 						})
// 					);
// 				} else {
// 					console.log('we did NOT receive the token back');
// 					this.handle401Outcomes(error);
// 					return throwError({
// 						detail: 'Server connection failure',
// 						code: 'unknown_connection_failure',
// 					});
// 				}
// 			})
// 		);
// 	}

// 	handle401Outcomes(error: HttpErrorResponse) {
// 		let failCode = null;
// 		let failMessage = '';
// 		let failUrl = '';
// 		let errObj;
// 		if (error && error.error) {
// 			errObj = error.error;
// 			if (errObj.code) {
// 				failCode = errObj.code;
// 			}
// 			if (errObj.detail) {
// 				failMessage = errObj.detail;
// 			}
// 			if (error.url) {
// 				failUrl = error.url;
// 			}
// 		}

// 		if (failCode === 'token_not_valid') {
// 			console.log('failUrl:');
// 			console.log(failUrl);
// 			const pos = failUrl.indexOf('auth/jwt/refresh/');
// 			if (pos > -1) {
// 				console.log('this failure was the result of a tried refresh token attempt.');

// 				// should not do another refresh try.
// 				// clear the failed token from storage
// 				this.authService.logout();

// 				this.toastService.show('Failed to refresh session. Please login again.');

// 				return of(error);
// 			}
// 		}
// 	}
// }
